// src/components/admin/EnterpriseDataUploader.jsx

import React, { useState } from 'react';
import { FileSpreadsheet, UploadCloud, AlertCircle, RefreshCw } from 'lucide-react';
import { METRICS, OWNERS, UNIT_LABELS, PERIOD_LABELS } from '../../data/workforceDictionary';
import { validateRows, buildPayload } from '../../utils/workforceCsv';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../config/firebase-config';

const EnterpriseDataUploader = ({ orgs }) => {
    const [selectedOrgId, setSelectedOrgId] = useState('');
    const [csvFile, setCsvFile] = useState(null);
    const [isUploadingCsv, setIsUploadingCsv] = useState(false);

    const handleCsvUpload = async () => {
        if (!selectedOrgId) {
            alert("Please select a Target Organization first.");
            return;
        }
        if (!csvFile) {
            alert("Please select a CSV file.");
            return;
        }

        setIsUploadingCsv(true);

        const reader = new FileReader();
        reader.onload = async (event) => {
            try {
                const text = event.target.result;
                // Same parser and checks as the console's Data Intake tab (utils/workforceCsv.js):
                // quoted fields, header spellings, dictionary ids, numbers.
                const checked = validateRows(text);
                if (checked.fileErrors.length) throw new Error(checked.fileErrors.join(' '));
                // A wide file (one column per figure) with no date column: this screen has
                // no period-end field to stand in, so one message rather than one per row.
                if (checked.layout === 'wide' && !checked.columns.some((c) => c.role === 'date')) throw new Error('Add a date column: this screen has no period-end field.');
                const bad = checked.rows.filter((r) => r.errors.length);
                if (bad.length) throw new Error(`${bad.length} row${bad.length === 1 ? '' : 's'} could not be read. Line ${bad[0].line}: ${bad[0].errors.join('; ')}${bad.length > 1 ? ` (and ${bad.length - 1} more)` : ''}.`);
                const dataPayload = buildPayload(checked.rows);
                if (dataPayload.length === 0) throw new Error('No rows with a date, metric and value were found.');

                // Call the Cloud Function
                const ingestFn = httpsCallable(functions, 'ingestCorporateMetrics');
                const res = await ingestFn({ 
                    orgId: selectedOrgId, 
                    metricsData: dataPayload 
                });

                const warnings = (checked.fileWarnings || []).length ? ` Worth a look: ${checked.fileWarnings.join(' ')}` : '';
                alert(`✅ Successfully synced ${res.data.rowsInserted} macro metrics to BigQuery!${warnings}`);
                setCsvFile(null); 

            } catch (error) {
                console.error("CSV Parse/Upload Error:", error);
                alert("❌ Upload Failed: " + error.message);
            } finally {
                setIsUploadingCsv(false);
            }
        };

        reader.onerror = () => {
            alert("Error reading file.");
            setIsUploadingCsv(false);
        };

        reader.readAsText(csvFile);
    };

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 animate-in fade-in">
            {/* Uploader Card */}
            <div className="bg-white p-6 md:p-8 rounded-3xl border border-slate-200 shadow-sm flex flex-col">
                <div className="flex items-center gap-3 mb-2">
                    <div className="p-3 bg-blue-50 text-blue-600 rounded-xl"><FileSpreadsheet size={24}/></div>
                    <h2 className="text-xl font-black text-slate-900">Upload EAP & HR Data</h2>
                </div>
                <p className="text-slate-500 text-sm mb-6">Aggregate macro workforce metrics directly into the Data Warehouse for AI analysis.</p>

                <div className="space-y-6 flex-1">
                    {/* Target Org Selection */}
                    <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Target Organization</label>
                        <select 
                            className="w-full p-3 rounded-xl border border-slate-200 bg-slate-50 font-medium focus:ring-2 focus:ring-blue-500 outline-none"
                            value={selectedOrgId} 
                            onChange={(e) => setSelectedOrgId(e.target.value)}
                        >
                            <option value="" disabled>-- Choose Organization --</option>
                            {orgs.map(org => <option key={org.id} value={org.id}>{org.name}</option>)}
                        </select>
                    </div>

                    {/* File Upload Area */}
                    <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">CSV File</label>
                        <div className="border-2 border-dashed border-slate-300 rounded-2xl p-8 flex flex-col items-center justify-center bg-slate-50 hover:bg-slate-100 transition-colors relative">
                            <UploadCloud size={40} className="text-slate-400 mb-4" />
                            <p className="text-sm font-bold text-slate-700 mb-1">
                                {csvFile ? csvFile.name : "Click or drag CSV here"}
                            </p>
                            <p className="text-xs text-slate-500">Max size 5MB</p>
                            <input 
                                type="file" 
                                accept=".csv"
                                onChange={(e) => setCsvFile(e.target.files[0])}
                                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                            />
                        </div>
                    </div>
                </div>

                <button 
                    onClick={handleCsvUpload}
                    disabled={isUploadingCsv || !selectedOrgId || !csvFile}
                    className="w-full mt-6 py-4 bg-slate-900 text-white rounded-xl font-bold flex justify-center items-center gap-2 hover:bg-black transition-colors disabled:opacity-50"
                >
                    {isUploadingCsv ? <RefreshCw className="animate-spin" size={20}/> : <UploadCloud size={20} />}
                    {isUploadingCsv ? "Processing & Syncing..." : "Sync to Data Warehouse"}
                </button>
            </div>

            {/* Instructions / Spec Card */}
            <div className="bg-slate-900 text-white p-6 md:p-8 rounded-3xl border border-slate-800 shadow-xl flex flex-col">
                <h3 className="text-lg font-bold flex items-center gap-2 mb-4 text-blue-300">
                    <AlertCircle size={20} /> CSV Formatting Rules
                </h3>
                <p className="text-slate-300 text-sm mb-6 leading-relaxed">
                    To ensure successful ingestion into the BigQuery Data Lake, your CSV must contain the following exact column headers (case-insensitive).
                </p>
                
                <div className="space-y-3 mb-6">
                    <div className="flex gap-4 p-3 bg-white/10 rounded-xl border border-white/5">
                        <code className="text-blue-300 text-xs font-bold w-16 shrink-0 mt-0.5">date</code>
                        <p className="text-xs text-slate-300">Format: YYYY-MM-DD. The date the metric was recorded.</p>
                    </div>
                    <div className="flex gap-4 p-3 bg-white/10 rounded-xl border border-white/5">
                        <code className="text-blue-300 text-xs font-bold w-16 shrink-0 mt-0.5">group</code>
                        <p className="text-xs text-slate-300">(Optional) The specific squad/department. Use "All" for company-wide.</p>
                    </div>
                    <div className="flex gap-4 p-3 bg-white/10 rounded-xl border border-white/5">
                        <code className="text-blue-300 text-xs font-bold w-16 shrink-0 mt-0.5">source</code>
                        <p className="text-xs text-slate-300">Origin of data (e.g., "Kronos", "GreenShield").</p>
                    </div>
                    <div className="flex gap-4 p-3 bg-white/10 rounded-xl border border-white/5">
                        <code className="text-blue-300 text-xs font-bold w-16 shrink-0 mt-0.5">metric</code>
                        <p className="text-xs text-slate-300">The metric name (e.g., "absenteeism_rate", "std_claims").</p>
                    </div>
                    <div className="flex gap-4 p-3 bg-white/10 rounded-xl border border-white/5">
                        <code className="text-blue-300 text-xs font-bold w-16 shrink-0 mt-0.5">value</code>
                        <p className="text-xs text-slate-300">Numeric value (e.g., 4.2, 150).</p>
                    </div>
                </div>

                <div className="mt-auto p-4 bg-blue-900/30 rounded-xl border border-blue-500/30">
                    <p className="text-xs text-blue-200 font-mono">
                        date, group, source, metric, value<br/>
                        2023-10-01, Pilots, Kronos, absenteeism_rate, 4.2<br/>
                        2023-10-01, All, GreenShield, std_claims, 15
                    </p>
                </div>
            </div>
        </div>
    );
};

// The workforce dictionary as a reference table: what a file may contain, by
// who owns the number, with the ids and aliases the uploader accepts.
export const DictionaryReference = () => (
    <div className="bg-white p-6 md:p-8 rounded-3xl border border-slate-200 shadow-sm">
        <h2 className="text-xl font-black text-slate-900">Workforce data dictionary</h2>
        <p className="text-slate-500 text-sm mt-1 mb-6">Every figure the console can hold, by who owns it. The <code className="text-xs bg-slate-100 px-1 rounded">metric</code> column of a CSV takes the id or any alias; the file's date is the period end, and a <code className="text-xs bg-slate-100 px-1 rounded">group</code> column breaks a figure out by team or unit, which is what the triangulation view reads.</p>
        <div className="space-y-6">
            {Object.values(OWNERS).map((o) => (
                <div key={o.id}>
                    <div className="flex items-baseline justify-between gap-3 mb-2">
                        <h3 className="text-sm font-black text-slate-800 uppercase tracking-wider">{o.label}</h3>
                        <span className="text-[11px] text-slate-400">{o.owner}</span>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                            <thead className="text-slate-400 text-left"><tr><th className="py-1 pr-3 font-semibold">Id</th><th className="py-1 pr-3 font-semibold">Figure</th><th className="py-1 pr-3 font-semibold">Unit · period</th><th className="py-1 font-semibold">Also accepted</th></tr></thead>
                            <tbody className="divide-y divide-slate-100">
                                {METRICS.filter((m) => m.owner === o.id).map((m) => (
                                    <tr key={m.id}>
                                        <td className="py-1.5 pr-3 font-mono text-[11px] text-slate-700 whitespace-nowrap">{m.id}</td>
                                        <td className="py-1.5 pr-3 text-slate-800">{m.label}</td>
                                        <td className="py-1.5 pr-3 text-slate-500 whitespace-nowrap">{UNIT_LABELS[m.unit] || m.unit} · {PERIOD_LABELS[m.period] || m.period}</td>
                                        <td className="py-1.5 text-slate-400">{m.aliases.join(', ') || '—'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            ))}
        </div>
    </div>
);

export default EnterpriseDataUploader;